import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button, Input } from '@img/ui';
import { api, type Paginated, type ApiSuccess } from '../../lib/api';

// ━━━━━━━━━━━━━━━━━━━━ Types ━━━━━━━━━━━━━━━━━━━━

interface BrokerRow {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  status: 'ACTIVE' | 'SUSPENDED' | 'DELETED';
  verified: boolean;
  verificationTier: string | null;
  walletBalance: number | string;
  totalLogins: number;
  lastLoginAt: string | null;
  createdAt: string;
  brokerProfile?: {
    subscriptionPlan: string;
    subscriptionExpiresAt: string | null;
    subscriptionDiscountPct: number | string | null;
    subscriptionDiscountFixed: number | string | null;
    subscriptionDiscountUntil: string | null;
    subscriptionDiscountReason: string | null;
    rating?: number | null;
    totalRatings?: number;
    totalProperties?: number;
    totalAuctions?: number;
  } | null;
}

interface Complaint {
  id: string;
  subject: string;
  details: string;
  status: 'new' | 'investigating' | 'resolved' | 'rejected';
  adminNotes: string | null;
  createdAt: string;
  resolvedAt: string | null;
  broker: { id: string; name: string; email: string | null };
  user: { id: string; name: string; email: string | null };
}

interface Review {
  id: string;
  rating: number;
  review: string | null;
  createdAt: string;
  broker: { id: string; name: string };
  user: { id: string; name: string };
}

const COMPLAINT_STATUS: Record<string, { label: string; color: 'amber' | 'navy' | 'green' | 'red' }> = {
  new: { label: 'جديدة', color: 'amber' },
  investigating: { label: 'قيد التحقيق', color: 'navy' },
  resolved: { label: 'محلولة', color: 'green' },
  rejected: { label: 'مرفوضة', color: 'red' },
};

const PLANS: Record<string, { label: string; color: 'gray' | 'navy' | 'gold' | 'green' }> = {
  FREE:       { label: 'مجاني',   color: 'gray' },
  BASIC:      { label: 'أساسي',   color: 'navy' },
  PRO:        { label: 'احترافي', color: 'gold' },
  ENTERPRISE: { label: 'مؤسسي',   color: 'green' },
};

// ━━━━━━━━━━━━━━━━━━━━ Main Component ━━━━━━━━━━━━━━━━━━━━

export function AdminBrokersPage() {
  const [tab, setTab] = useState<'list' | 'applications' | 'complaints' | 'reviews'>('list');
  const [openCreate, setOpenCreate] = useState(false);
  const qc = useQueryClient();

  return (
    <div>
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black text-navy-dark">🤝 إدارة المسوّقين</h1>
          <p className="text-sm text-ink-muted mt-1">قائمة كاملة + شكاوى + تقييمات + إضافة/تصدير + كل أزرار التحكم</p>
        </div>
        {/* ➕ زر إضافة مسوّق + 📤 تصدير */}
        <div className="flex gap-2 flex-wrap">
          <Button variant="ghost" onClick={() => exportBrokersCSV()}>📤 تصدير CSV</Button>
          <Button variant="primary" onClick={() => setOpenCreate(true)}>➕ إضافة مسوّق جديد</Button>
        </div>
      </div>

      <div className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto">
        <Tab active={tab === 'list'} onClick={() => setTab('list')}>👥 المسوّقون</Tab>
        <Tab active={tab === 'applications'} onClick={() => setTab('applications')}>📥 طلبات التحوّل</Tab>
        <Tab active={tab === 'complaints'} onClick={() => setTab('complaints')}>🚨 الشكاوى</Tab>
        <Tab active={tab === 'reviews'} onClick={() => setTab('reviews')}>⭐ التقييمات</Tab>
      </div>

      {tab === 'list' && <BrokersList />}
      {tab === 'applications' && <ApplicationsList />}
      {tab === 'complaints' && <ComplaintsList />}
      {tab === 'reviews' && <ReviewsList />}

      {/* مودال إضافة مسوّق جديد */}
      {openCreate && (
        <CreateBrokerModal
          onClose={() => setOpenCreate(false)}
          onCreated={() => {
            qc.invalidateQueries({ queryKey: ['admin', 'brokers'] });
            setOpenCreate(false);
          }}
        />
      )}
    </div>
  );
}

/* 📤 تصدير CSV — جلب كل المسوّقين وتنزيلهم كملف CSV */
async function exportBrokersCSV() {
  try {
    const res = await api.get<Paginated<BrokerRow>>('/api/admin/users', {
      query: { role: 'BROKER', limit: 1000 },
    });
    const rows = res.data ?? [];
    const headers = ['الاسم', 'الإيميل', 'الجوال', 'الحالة', 'الخطة', 'موثّق', 'المحفظة', 'تاريخ التسجيل'];
    const csvRows = rows.map((b) => [
      b.name,
      b.email ?? '',
      b.phone ?? '',
      b.status,
      b.brokerProfile?.subscriptionPlan ?? 'FREE',
      b.verified ? 'نعم' : 'لا',
      Number(b.walletBalance).toLocaleString('en-US'),
      new Date(b.createdAt).toLocaleDateString('ar-SA'),
    ]);
    const csv = '﻿' + [headers, ...csvRows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `brokers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    alert('فشل التصدير: ' + (e as Error).message);
  }
}

/* 🛡️ خطوات التحقق من أهلية المسوّق (نفس فلسفة مراجعة العقار) */
interface EligibilityChecklist {
  identity: boolean;       // تحقق الهوية
  falLicense: boolean;     // تحقق رخصة فال (eservicesredp.rega.gov.sa)
  contactVerified: boolean; // تحقق من الاتصال (إيميل/جوال)
  noConflict: boolean;     // عدم وجود سجل مخالفات
}
const EMPTY_CHECKLIST: EligibilityChecklist = {
  identity: false, falLicense: false, contactVerified: false, noConflict: false,
};

/* مكوّن مشترك — يعرض حقول رخصة فال + checklist الأهلية */
function EligibilityFields({
  fal, setFal, checklist, setChecklist, compact = false,
}: {
  fal: { number: string; expiresAt: string };
  setFal: (f: { number: string; expiresAt: string }) => void;
  checklist: EligibilityChecklist;
  setChecklist: (c: EligibilityChecklist) => void;
  compact?: boolean;
}) {
  const allDone = Object.values(checklist).every(Boolean);
  return (
    <fieldset className="border-2 border-amber-200 bg-amber-50/40 rounded-xl p-4">
      <legend className="px-2 font-black text-amber-900 text-sm">🛡️ التحقق من الأهلية ورخصة فال</legend>

      <div className="space-y-3 mt-2">
        {/* رخصة فال */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input
            label="رقم رخصة فال *"
            value={fal.number}
            onChange={(e) => setFal({ ...fal, number: e.target.value })}
            dir="ltr"
            placeholder="1100012345"
            hint="رقم رخصة فال الصادرة من الهيئة العامة للعقار"
          />
          <Input
            label="تاريخ انتهاء الرخصة *"
            type="date"
            value={fal.expiresAt}
            onChange={(e) => setFal({ ...fal, expiresAt: e.target.value })}
          />
        </div>

        <a
          href="https://eservicesredp.rega.gov.sa/auth/queries/Brokerage"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-xs text-navy-dark hover:underline"
        >
          🔗 افتح بوابة الهيئة للتحقق من رخصة فال ←
        </a>

        {/* checklist */}
        <div className={`grid grid-cols-1 ${compact ? 'md:grid-cols-2' : 'md:grid-cols-2'} gap-2 mt-3`}>
          <CheckItem
            checked={checklist.identity}
            onChange={(v) => setChecklist({ ...checklist, identity: v })}
            label="تأكدت من هوية المسوّق (هوية وطنية/إقامة)"
          />
          <CheckItem
            checked={checklist.falLicense}
            onChange={(v) => setChecklist({ ...checklist, falLicense: v })}
            label="تحققت من رخصة فال على بوابة REGA"
          />
          <CheckItem
            checked={checklist.contactVerified}
            onChange={(v) => setChecklist({ ...checklist, contactVerified: v })}
            label="تواصلت معه وتأكدت من الإيميل/الجوال"
          />
          <CheckItem
            checked={checklist.noConflict}
            onChange={(v) => setChecklist({ ...checklist, noConflict: v })}
            label="لا يوجد سجل مخالفات أو شكاوى عليه"
          />
        </div>

        <div className={`text-xs font-bold rounded-lg px-3 py-2 ${
          allDone ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-amber-100 text-amber-900 border border-amber-300'
        }`}>
          {allDone
            ? '✓ اكتملت جميع خطوات التحقق'
            : `⚠️ أكملت ${Object.values(checklist).filter(Boolean).length} من 4 خطوات قبل الترقية`}
        </div>
      </div>
    </fieldset>
  );
}

function CheckItem({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label className={`flex items-start gap-2 p-2.5 rounded-lg border-2 cursor-pointer transition ${
      checked ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-white hover:bg-gray-50'
    }`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 mt-0.5 accent-green-600"
      />
      <span className="text-xs font-bold text-ink leading-snug">{label}</span>
    </label>
  );
}

/* ➕ مودال إضافة مسوّق — 3 أوضاع: ترقية مستخدم / دعوة بإيميل / إنشاء مباشر */
function CreateBrokerModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [mode, setMode] = useState<'promote' | 'invite' | 'create'>('promote');

  // وضع الدعوة (يرسل دعوة عبر الإيميل) — رخصة فال اختيارية بالدعوة (ينمنحها بعد القبول)
  const [invite, setInvite] = useState({ email: '', phone: '', name: '', message: '' });
  const [inviteFal, setInviteFal] = useState({ number: '', expiresAt: '' });
  const [inviteChecklist, setInviteChecklist] = useState<EligibilityChecklist>(EMPTY_CHECKLIST);
  const inviteM = useMutation({
    mutationFn: () => api.post('/api/admin/invitations', {
      email: invite.email.trim() || undefined,
      phone: invite.phone.trim() || undefined,
      name: invite.name.trim() || undefined,
      role: 'BROKER',
      message: invite.message.trim() || 'تمت دعوتك للانضمام كمسوّق عقاري على منصة شركة صورة العقارية',
      // رخصة فال + checklist تُرفق بالدعوة (تُستخدم بعد القبول)
      falLicenseNumber: inviteFal.number || undefined,
      falLicenseExpiresAt: inviteFal.expiresAt ? new Date(inviteFal.expiresAt).toISOString() : undefined,
      eligibilityChecklist: inviteChecklist,
    }),
    onSuccess: onCreated,
  });
  const inviteEligibilityOk = !!inviteFal.number && !!inviteFal.expiresAt && Object.values(inviteChecklist).every(Boolean);

  // وضع الإنشاء المباشر (يُنشئ المستخدم فوراً) — رخصة فال إلزامية
  const [create, setCreate] = useState({
    name: '', email: '', phone: '', password: '', plan: 'FREE',
  });
  const [createFal, setCreateFal] = useState({ number: '', expiresAt: '' });
  const [createChecklist, setCreateChecklist] = useState<EligibilityChecklist>(EMPTY_CHECKLIST);
  const createM = useMutation({
    mutationFn: async () => {
      // 1) إنشاء المستخدم بدور مسوّق + رخصة فال + checklist
      const created = await api.post<{ data: { id: string } }>('/api/admin/users', {
        name: create.name.trim(),
        email: create.email.trim() || undefined,
        phone: create.phone.trim() || undefined,
        password: create.password,
        role: 'BROKER',
        verified: true,
        status: 'ACTIVE',
        falLicenseNumber: createFal.number,
        falLicenseExpiresAt: new Date(createFal.expiresAt).toISOString(),
        falLicenseStatus: 'APPROVED',
        falLicenseReviewedAt: new Date().toISOString(),
        eligibilityChecklist: createChecklist,
      });
      // 2) تعيين الخطة الابتدائية
      if (created?.data?.id) {
        await api.put(`/api/admin/users/${created.data.id}/broker-discount`, { plan: create.plan }).catch(() => null);
      }
      return created;
    },
    onSuccess: onCreated,
  });
  const createEligibilityOk =
    !!create.name && !!create.password && (!!create.email || !!create.phone) &&
    !!createFal.number && !!createFal.expiresAt &&
    Object.values(createChecklist).every(Boolean) &&
    new Date(createFal.expiresAt) > new Date();

  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-l from-green-700 to-green-500 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center text-xl">➕</div>
            <h2 className="font-black text-lg">إضافة مسوّق جديد</h2>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white text-xl">✕</button>
        </div>

        {/* اختيار النمط */}
        <div className="px-4 pt-4 flex gap-2 flex-wrap">
          <button
            onClick={() => setMode('promote')}
            className={`flex-1 min-w-[120px] px-3 py-2.5 rounded-xl border-2 text-xs md:text-sm font-bold transition ${
              mode === 'promote' ? 'border-navy bg-navy-soft text-navy-dark' : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            🔎 ترقية مستخدم موجود
          </button>
          <button
            onClick={() => setMode('invite')}
            className={`flex-1 min-w-[120px] px-3 py-2.5 rounded-xl border-2 text-xs md:text-sm font-bold transition ${
              mode === 'invite' ? 'border-navy bg-navy-soft text-navy-dark' : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            📧 دعوة جديدة
          </button>
          <button
            onClick={() => setMode('create')}
            className={`flex-1 min-w-[120px] px-3 py-2.5 rounded-xl border-2 text-xs md:text-sm font-bold transition ${
              mode === 'create' ? 'border-navy bg-navy-soft text-navy-dark' : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            👤 إنشاء مباشر
          </button>
        </div>

        <div className="p-6 space-y-3">
          {mode === 'promote' && (
            <PromoteExistingUserPanel onPromoted={onCreated} onClose={onClose} />
          )}
          {mode === 'invite' && (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-900">
                💡 يصل المسوّق رابط دعوة بالإيميل/الجوال. يكمل التسجيل بكلمة مرور خاصة به. تُحفظ بيانات رخصة فال + checklist معه.
              </div>
              <Input label="الاسم (اختياري)" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} />
              <Input label="الإيميل" type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} dir="ltr" />
              <Input label="الجوال" value={invite.phone} onChange={(e) => setInvite({ ...invite, phone: e.target.value })} dir="ltr" hint="بصيغة دولية مثلاً 0501234567" />
              <div>
                <label className="block text-sm font-bold text-ink mb-1">رسالة مخصّصة (اختياري)</label>
                <textarea
                  value={invite.message}
                  onChange={(e) => setInvite({ ...invite, message: e.target.value })}
                  rows={3}
                  placeholder="ترحيب أو تعليمات…"
                  className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm"
                />
              </div>

              {/* 🛡️ خطوات الأهلية + رخصة فال */}
              <EligibilityFields fal={inviteFal} setFal={setInviteFal} checklist={inviteChecklist} setChecklist={setInviteChecklist} />

              {inviteM.isError && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">
                  {(inviteM.error as Error)?.message ?? 'فشل إرسال الدعوة'}
                </div>
              )}
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="ghost" onClick={onClose}>إلغاء</Button>
                <Button
                  variant="primary"
                  onClick={() => inviteM.mutate()}
                  loading={inviteM.isPending}
                  disabled={(!invite.email && !invite.phone) || !inviteEligibilityOk}
                >
                  📧 إرسال الدعوة
                </Button>
              </div>
            </>
          )}
          {mode === 'create' && (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
                ⚠️ سيتم إنشاء الحساب فوراً وتفعيله. تأكد من أن المسوّق وافق على الشروط ومن صحة رخصة فال.
              </div>
              <Input label="الاسم *" value={create.name} onChange={(e) => setCreate({ ...create, name: e.target.value })} required />
              <Input label="الإيميل" type="email" value={create.email} onChange={(e) => setCreate({ ...create, email: e.target.value })} dir="ltr" />
              <Input label="الجوال" value={create.phone} onChange={(e) => setCreate({ ...create, phone: e.target.value })} dir="ltr" />
              <Input label="كلمة المرور المؤقتة *" type="password" value={create.password} onChange={(e) => setCreate({ ...create, password: e.target.value })} required dir="ltr" hint="6 أحرف على الأقل — سيتم إرسالها للمسوّق وعليه تغييرها أول دخول" />
              <div>
                <label className="block text-sm font-bold text-ink mb-1">الخطة الابتدائية</label>
                <select
                  value={create.plan}
                  onChange={(e) => setCreate({ ...create, plan: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm"
                >
                  <option value="FREE">مجاني</option>
                  <option value="BASIC">أساسي</option>
                  <option value="PRO">احترافي</option>
                  <option value="ENTERPRISE">مؤسسي</option>
                </select>
              </div>

              {/* 🛡️ خطوات الأهلية + رخصة فال (إلزامية للإنشاء المباشر) */}
              <EligibilityFields fal={createFal} setFal={setCreateFal} checklist={createChecklist} setChecklist={setCreateChecklist} />

              {createM.isError && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">
                  {(createM.error as Error)?.message ?? 'فشل الإنشاء'}
                </div>
              )}
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="ghost" onClick={onClose}>إلغاء</Button>
                <Button
                  variant="primary"
                  onClick={() => createM.mutate()}
                  loading={createM.isPending}
                  disabled={!createEligibilityOk}
                >
                  ✓ إنشاء المسوّق
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* 🔎 تاب ترقية مستخدم موجود إلى مسوّق */
interface UserSearchResult {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  role: string;
  status: string;
  verified: boolean;
  createdAt: string;
}

function PromoteExistingUserPanel({ onPromoted, onClose }: { onPromoted: () => void; onClose: () => void }) {
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<UserSearchResult | null>(null);
  const [plan, setPlan] = useState<'FREE' | 'BASIC' | 'PRO' | 'ENTERPRISE'>('FREE');
  const [confirm, setConfirm] = useState(false);
  const [fal, setFal] = useState({ number: '', expiresAt: '' });
  const [checklist, setChecklist] = useState<EligibilityChecklist>(EMPTY_CHECKLIST);

  // 🔍 بحث المستخدمين (USER فقط لأن BROKER/ADMIN ما يحتاجون ترقية)
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'users-search', q],
    queryFn: () => api.get<Paginated<UserSearchResult>>('/api/admin/users', {
      query: { q, role: 'USER', limit: 15 },
    }),
    enabled: q.trim().length >= 2,
  });

  const allChecksDone = Object.values(checklist).every(Boolean);
  const eligibilityOk = !!fal.number && !!fal.expiresAt && allChecksDone && new Date(fal.expiresAt) > new Date();

  const promoteM = useMutation({
    mutationFn: async () => {
      if (!selected) throw new Error('لم يتم اختيار مستخدم');
      // 1) ترقية الدور + حفظ بيانات رخصة فال + checklist
      await api.put(`/api/admin/users/${selected.id}`, {
        role: 'BROKER',
        falLicenseNumber: fal.number,
        falLicenseExpiresAt: new Date(fal.expiresAt).toISOString(),
        falLicenseStatus: 'APPROVED',
        falLicenseReviewedAt: new Date().toISOString(),
        eligibilityChecklist: checklist,
        verified: true,
      });
      // 2) تعيين الخطة الابتدائية
      await api.put(`/api/admin/users/${selected.id}/broker-discount`, { plan }).catch(() => null);
      return null;
    },
    onSuccess: () => {
      onPromoted();
    },
  });

  const list = data?.data ?? [];

  return (
    <>
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-900">
        💡 ابحث عن مستخدم بالاسم/الإيميل/الجوال، اخترهُ، وحدّد خطته — وستتم ترقيته فوراً إلى مسوّق.
      </div>

      {/* خانة البحث */}
      <div>
        <label className="block text-sm font-bold text-ink mb-1">🔍 بحث المستخدمين</label>
        <Input
          placeholder="اسم/إيميل/جوال (يبدأ البحث بعد حرفين)…"
          value={q}
          onChange={(e) => { setQ(e.target.value); setSelected(null); }}
          dir="auto"
        />
      </div>

      {/* قائمة النتائج */}
      {q.trim().length >= 2 && (
        <div className="border border-gray-200 rounded-xl max-h-64 overflow-y-auto">
          {isLoading ? (
            <div className="text-center py-8 text-xs text-ink-muted">⟳ جاري البحث…</div>
          ) : list.length === 0 ? (
            <div className="text-center py-8 text-xs text-ink-muted">
              <div className="text-3xl mb-1 opacity-40">🔍</div>
              لا يوجد مستخدمون يطابقون "{q}"
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {list.map((u) => (
                <li key={u.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(u)}
                    className={`w-full text-start px-3 py-2.5 hover:bg-gray-50 transition flex items-center gap-3 ${
                      selected?.id === u.id ? 'bg-navy-soft' : ''
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center font-black text-sm shrink-0 ${
                      selected?.id === u.id ? 'bg-navy text-white' : 'bg-gradient-to-br from-navy-light to-navy text-white'
                    }`}>
                      {u.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-navy-dark text-sm truncate flex items-center gap-1.5">
                        {u.name}
                        {u.verified && <span className="text-green-600 text-[10px]">✓</span>}
                        {u.status === 'SUSPENDED' && <Badge color="amber">موقوف</Badge>}
                      </div>
                      <div className="text-[11px] text-ink-muted truncate" dir="ltr">
                        {u.email ?? u.phone ?? '—'}
                      </div>
                    </div>
                    {selected?.id === u.id && (
                      <span className="text-navy text-xl shrink-0">✓</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* بطاقة المستخدم المختار + خيار الخطة */}
      {selected && (
        <div className="border-2 border-navy-soft bg-navy-soft/30 rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-navy text-white flex items-center justify-center font-black text-lg shrink-0">
              {selected.name.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-black text-navy-dark">{selected.name}</div>
              <div className="text-xs text-ink-muted truncate" dir="ltr">{selected.email ?? selected.phone}</div>
            </div>
            <Badge color="navy">سيُرقّى</Badge>
          </div>

          <div>
            <label className="block text-xs font-bold text-ink mb-1">الخطة الابتدائية</label>
            <select
              value={plan}
              onChange={(e) => setPlan(e.target.value as typeof plan)}
              className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm"
            >
              <option value="FREE">مجاني</option>
              <option value="BASIC">أساسي</option>
              <option value="PRO">احترافي</option>
              <option value="ENTERPRISE">مؤسسي</option>
            </select>
          </div>

          <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">
            ⚠️ بعد الترقية: يصبح للمستخدم صلاحيات نشر العقارات + إدارة الإعلانات. يمكنه الدخول للوحة المسوّق فوراً.
          </div>
        </div>
      )}

      {/* 🛡️ خطوات التحقق من الأهلية + رخصة فال */}
      {selected && (
        <EligibilityFields fal={fal} setFal={setFal} checklist={checklist} setChecklist={setChecklist} />
      )}

      {promoteM.isError && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">
          {(promoteM.error as Error)?.message ?? 'فشلت الترقية'}
        </div>
      )}

      <div className="flex gap-2 justify-end pt-2">
        <Button variant="ghost" onClick={onClose}>إلغاء</Button>
        {!confirm ? (
          <Button
            variant="primary"
            onClick={() => setConfirm(true)}
            disabled={!selected || !eligibilityOk}
          >
            ⬆️ ترقية إلى مسوّق
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={() => setConfirm(false)}>تراجع</Button>
            <Button
              variant="primary"
              onClick={() => promoteM.mutate()}
              loading={promoteM.isPending}
              className="!bg-green-600 hover:!bg-green-700"
            >
              ✓ تأكيد الترقية
            </Button>
          </>
        )}
      </div>
    </>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ قائمة المسوّقين ━━━━━━━━━━━━━━━━━━━━

function BrokersList() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ q: '', status: '', plan: '', verified: '', page: 1 });
  const [openId, setOpenId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'brokers', 'list', filters],
    queryFn: () => api.get<Paginated<BrokerRow>>('/api/admin/users', {
      query: { ...filters, role: 'BROKER', limit: 30 },
    }),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'brokers'] });

  const list = data?.data ?? [];

  // 📊 إحصاءات
  const total = data?.pagination?.total ?? 0;
  const counts = {
    active:    list.filter((b) => b.status === 'ACTIVE').length,
    suspended: list.filter((b) => b.status === 'SUSPENDED').length,
    verified:  list.filter((b) => b.verified).length,
    diamond:   list.filter((b) => b.verificationTier === 'DIAMOND').length,
  };

  return (
    <>
      {/* 📊 شارات الإحصاءات */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="الإجمالي"  value={total}            color="navy"  icon="🤝" />
        <StatCard label="نشطون"     value={counts.active}    color="green" icon="✓"
          active={filters.status === 'ACTIVE'} onClick={() => setFilters({ ...filters, status: filters.status === 'ACTIVE' ? '' : 'ACTIVE', page: 1 })} />
        <StatCard label="موقّفون"   value={counts.suspended} color="amber" icon="⏸"
          active={filters.status === 'SUSPENDED'} onClick={() => setFilters({ ...filters, status: filters.status === 'SUSPENDED' ? '' : 'SUSPENDED', page: 1 })} />
        <StatCard label="موثّقون"   value={counts.verified}  color="gold"  icon="⭐"
          active={filters.verified === 'true'} onClick={() => setFilters({ ...filters, verified: filters.verified === 'true' ? '' : 'true', page: 1 })} />
      </div>

      {/* 🔍 الفلاتر */}
      <Card className="mb-5">
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2">
              <Input placeholder="🔍 بحث (الاسم/الإيميل/الجوال)…"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value, page: 1 })}
              />
            </div>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
              className="px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm"
            >
              <option value="">كل الحالات</option>
              <option value="ACTIVE">نشط</option>
              <option value="SUSPENDED">موقوف</option>
              <option value="DELETED">محذوف</option>
            </select>
            <select
              value={filters.plan}
              onChange={(e) => setFilters({ ...filters, plan: e.target.value, page: 1 })}
              className="px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm"
            >
              <option value="">كل الخطط</option>
              {Object.entries(PLANS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
        </CardBody>
      </Card>

      {/* 📋 قائمة المسوّقين كبطاقات */}
      {isLoading ? (
        <div className="text-center py-12 text-ink-muted">⟳ جاري التحميل…</div>
      ) : list.length === 0 ? (
        <Card><CardBody>
          <div className="text-center py-12 text-ink-muted">
            <div className="text-5xl mb-2">🤝</div>
            <p>لا يوجد مسوّقون يطابقون البحث</p>
          </div>
        </CardBody></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {list.map((b) => <BrokerCard key={b.id} broker={b} onOpen={() => setOpenId(b.id)} onChanged={invalidate} />)}
        </div>
      )}

      {/* Pagination */}
      {data && data.pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <Button size="sm" variant="outline" disabled={filters.page <= 1} onClick={() => setFilters({ ...filters, page: filters.page - 1 })}>السابق</Button>
          <span className="px-4 py-2 text-sm font-bold">{filters.page} / {data.pagination.totalPages}</span>
          <Button size="sm" variant="outline" disabled={filters.page >= data.pagination.totalPages} onClick={() => setFilters({ ...filters, page: filters.page + 1 })}>التالي</Button>
        </div>
      )}

      {/* 🛠️ مودال التحكم */}
      {openId && (
        <BrokerControlModal
          brokerId={openId}
          onClose={() => setOpenId(null)}
          onChanged={invalidate}
        />
      )}
    </>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ بطاقة المسوّق ━━━━━━━━━━━━━━━━━━━━

function BrokerCard({ broker, onOpen, onChanged }: { broker: BrokerRow; onOpen: () => void; onChanged: () => void }) {
  const bp = broker.brokerProfile;
  const planInfo = bp ? PLANS[bp.subscriptionPlan] : null;
  const subExpired = bp?.subscriptionExpiresAt && new Date(bp.subscriptionExpiresAt) < new Date();
  const hasDiscount = bp?.subscriptionDiscountPct || bp?.subscriptionDiscountFixed;

  const toggleStatus = useMutation({
    mutationFn: () => api.put(`/api/admin/users/${broker.id}`, {
      status: broker.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE',
    }),
    onSuccess: onChanged,
  });

  return (
    <Card className={broker.status === 'SUSPENDED' ? 'border-amber-300 bg-amber-50/30' : ''}>
      <CardBody>
        {/* 👤 معلومات أساسية */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-700 to-green-500 text-white flex items-center justify-center font-black text-lg shrink-0">
            {broker.name.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h3 className="font-black text-navy-dark truncate">{broker.name}</h3>
              {broker.verified && <Badge color="green">✓</Badge>}
              {broker.verificationTier && <Badge color="gold">{broker.verificationTier}</Badge>}
              {broker.status === 'SUSPENDED' && <Badge color="amber">موقوف</Badge>}
            </div>
            <div className="text-xs text-ink-muted truncate" dir="ltr">{broker.email ?? broker.phone ?? '—'}</div>
          </div>
        </div>

        {/* 📊 معلومات الاشتراك */}
        {planInfo && bp && (
          <div className="bg-gray-50 rounded-xl p-3 mb-3 border border-gray-200">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-ink-muted">الخطة</span>
              <Badge color={planInfo.color}>{planInfo.label}</Badge>
            </div>
            {bp.subscriptionExpiresAt && (
              <div className="flex items-center justify-between">
                <span className="text-xs text-ink-muted">تنتهي في</span>
                <span className={`text-xs font-bold ${subExpired ? 'text-red-600' : 'text-ink'}`}>
                  {new Date(bp.subscriptionExpiresAt).toLocaleDateString('ar-SA')}
                  {subExpired && ' ⚠️'}
                </span>
              </div>
            )}
            {hasDiscount && (
              <div className="text-[11px] text-green-700 mt-1">
                🎁 خصم {bp.subscriptionDiscountPct ? `${bp.subscriptionDiscountPct}%` : ''}
                {bp.subscriptionDiscountFixed ? ` ${bp.subscriptionDiscountFixed} ر.س` : ''}
              </div>
            )}
          </div>
        )}

        {/* 📈 إحصاءات الأداء */}
        <div className="grid grid-cols-3 gap-1.5 mb-3 text-center">
          <Stat label="عقارات" value={bp?.totalProperties ?? 0} />
          <Stat label="مزادات" value={bp?.totalAuctions ?? 0} />
          <Stat label="تقييم" value={bp?.rating ? `${Number(bp.rating).toFixed(1)}⭐` : '—'} />
        </div>

        {/* 💰 المحفظة */}
        <div className="flex items-center justify-between text-xs mb-3 px-2">
          <span className="text-ink-muted">المحفظة:</span>
          <span className="font-bold text-navy-dark">{Number(broker.walletBalance).toLocaleString('en-US')} ر.س</span>
        </div>

        {/* 🛠️ أزرار التحكم — صفّان كاملان */}
        <div className="grid grid-cols-2 gap-2 mt-3">
          <Button size="sm" variant="primary" onClick={onOpen}>
            ⚙️ تحكم كامل
          </Button>
          <Link to={`/admin/users?id=${broker.id}`}>
            <Button size="sm" variant="ghost" className="!w-full">👤 الملف</Button>
          </Link>
        </div>

        {/* صفّ الإجراءات السريعة */}
        <div className="grid grid-cols-4 gap-1.5 mt-2">
          <button
            onClick={() => toggleStatus.mutate()}
            disabled={toggleStatus.isPending}
            title={broker.status === 'ACTIVE' ? 'إيقاف' : 'تفعيل'}
            className={`px-2 py-1.5 rounded-lg text-xs font-bold border transition ${
              broker.status === 'ACTIVE'
                ? 'border-amber-300 text-amber-700 hover:bg-amber-50'
                : 'border-green-300 text-green-700 hover:bg-green-50'
            }`}
          >
            {broker.status === 'ACTIVE' ? '⏸' : '✓'}
          </button>
          {broker.phone ? (
            <a
              href={`tel:${broker.phone}`}
              title="اتصل"
              className="px-2 py-1.5 rounded-lg text-xs font-bold border border-blue-200 text-blue-700 hover:bg-blue-50 text-center"
            >
              📞
            </a>
          ) : (
            <span className="px-2 py-1.5 rounded-lg text-xs border border-gray-200 text-gray-400 text-center cursor-not-allowed">📞</span>
          )}
          {broker.phone ? (
            <a
              href={`https://wa.me/${broker.phone.replace(/^[+0]/, '966')}`}
              target="_blank"
              rel="noreferrer"
              title="واتساب"
              className="px-2 py-1.5 rounded-lg text-xs font-bold border border-green-200 text-green-700 hover:bg-green-50 text-center"
            >
              💬
            </a>
          ) : (
            <span className="px-2 py-1.5 rounded-lg text-xs border border-gray-200 text-gray-400 text-center cursor-not-allowed">💬</span>
          )}
          {broker.email ? (
            <a
              href={`mailto:${broker.email}`}
              title="إيميل"
              className="px-2 py-1.5 rounded-lg text-xs font-bold border border-purple-200 text-purple-700 hover:bg-purple-50 text-center"
            >
              📧
            </a>
          ) : (
            <span className="px-2 py-1.5 rounded-lg text-xs border border-gray-200 text-gray-400 text-center cursor-not-allowed">📧</span>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ مودال التحكم الكامل ━━━━━━━━━━━━━━━━━━━━

function BrokerControlModal({ brokerId, onClose, onChanged }: { brokerId: string; onClose: () => void; onChanged: () => void }) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'subscription' | 'discount' | 'security' | 'admin' | 'listings'>('listings');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'broker', brokerId],
    queryFn: () => api.get<ApiSuccess<BrokerRow & {
      adminNote?: string | null;
      customBadge?: string | null;
      customBadgeColor?: string | null;
      bidCap?: number | string | null;
      blacklisted?: boolean;
    }>>(`/api/admin/users/${brokerId}`),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'broker', brokerId] });
    onChanged();
  };

  if (isLoading || !data?.data) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 text-gray-500">⟳ جاري التحميل…</div>
      </div>
    );
  }

  const broker = data.data;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        {/* رأس */}
        <div className="bg-gradient-to-l from-green-700 to-green-500 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center text-2xl shrink-0">🤝</div>
            <div className="min-w-0">
              <h2 className="font-black text-lg truncate">{broker.name}</h2>
              <p className="text-xs text-white/80" dir="ltr">{broker.email ?? broker.phone}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white text-xl shrink-0">✕</button>
        </div>

        {/* تبويبات */}
        <div className="border-b border-gray-200 px-4 flex gap-1 overflow-x-auto">
          <SubTab active={tab === 'listings'} onClick={() => setTab('listings')}>🏠 إعلاناته</SubTab>
          <SubTab active={tab === 'subscription'} onClick={() => setTab('subscription')}>💎 الاشتراك</SubTab>
          <SubTab active={tab === 'discount'} onClick={() => setTab('discount')}>🎁 الخصم</SubTab>
          <SubTab active={tab === 'security'} onClick={() => setTab('security')}>🔒 الأمان</SubTab>
          <SubTab active={tab === 'admin'} onClick={() => setTab('admin')}>⚙️ خصائص</SubTab>
        </div>

        <div className="p-6">
          {tab === 'listings' && <ListingsControlTab brokerId={broker.id} brokerName={broker.name} onSaved={refresh} />}
          {tab === 'subscription' && <SubscriptionTab broker={broker} onSaved={refresh} />}
          {tab === 'discount' && <DiscountTab broker={broker} onSaved={refresh} />}
          {tab === 'security' && <SecurityTab broker={broker} onSaved={refresh} />}
          {tab === 'admin' && <AdminPropsTab broker={broker} onSaved={refresh} />}
        </div>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ تبويب التحكم بالإعلانات (الجديد!) ━━━━━━━━━━━━━━━━━━━━

interface ListingsData {
  properties: Array<{
    id: string; title: string; status: string; type: string; purpose: string;
    price: number | string; city: string | null; featured: boolean; createdAt: string; coverImage: string | null;
  }>;
  auctions: Array<{ id: string; title: string; status: string; currentBid: number | string; totalBids: number }>;
  stats: { total: number; active: number; pending: number; archived: number; auctionsLive: number };
}

function ListingsControlTab({ brokerId, brokerName, onSaved }: { brokerId: string; brokerName: string; onSaved: () => void }) {
  const qc = useQueryClient();
  const [scope, setScope] = useState<'all' | 'active' | 'pending' | 'archived'>('all');
  const [reason, setReason] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'broker-listings', brokerId],
    queryFn: () => api.get<ApiSuccess<ListingsData>>(`/api/admin/users/${brokerId}/listings`),
  });

  const bulkAction = useMutation({
    mutationFn: (action: 'pause' | 'unpause' | 'archive' | 'delete') =>
      api.post<ApiSuccess<{ affected: number }>>(`/api/admin/users/${brokerId}/listings/bulk-action`, {
        action, scope, reason: reason.trim() || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'broker-listings', brokerId] });
      onSaved();
      setReason('');
      setConfirmDelete(false);
    },
  });

  const stats = data?.data?.stats;
  const properties = data?.data?.properties ?? [];

  // 📊 العقارات حسب الحالة المختارة
  const filteredCount = scope === 'all' ? stats?.total ?? 0 :
    scope === 'active' ? stats?.active ?? 0 :
    scope === 'pending' ? stats?.pending ?? 0 :
    stats?.archived ?? 0;

  return (
    <div className="space-y-4">
      {/* 📊 إحصاءات الإعلانات */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Stat label="إجمالي" value={stats.total} />
          <Stat label="نشطة" value={stats.active} />
          <Stat label="بانتظار" value={stats.pending} />
          <Stat label="مؤرشفة" value={stats.archived} />
        </div>
      )}

      {/* 🎯 اختيار النطاق */}
      <fieldset className="border border-gray-200 rounded-xl p-4">
        <legend className="px-2 font-black text-navy-dark text-sm">🎯 نطاق الإجراء</legend>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
          {(['all', 'active', 'pending', 'archived'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setScope(s)}
              className={`p-2.5 rounded-xl border-2 text-center text-xs font-bold transition ${
                scope === s
                  ? 'border-navy bg-navy-soft text-navy-dark'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {s === 'all' ? '🏘️ الكل' : s === 'active' ? '✓ النشطة' : s === 'pending' ? '⏳ بانتظار' : '🗄️ مؤرشفة'}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-ink-muted mt-2">سيتم تطبيق الإجراء على <b className="text-navy-dark">{filteredCount}</b> إعلان</p>
      </fieldset>

      {/* 📝 سبب (اختياري — يصل في إيميل المسوّق) */}
      <fieldset className="border border-gray-200 rounded-xl p-4">
        <legend className="px-2 font-black text-navy-dark text-sm">📝 سبب الإجراء (اختياري — يُرسل للمسوّق)</legend>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          placeholder="مثلاً: مخالفة شروط النشر، طلب من المسوّق، صيانة دورية…"
          className="w-full mt-2 px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm"
        />
      </fieldset>

      {/* 🛠️ أزرار الإجراءات */}
      <fieldset className="border-2 border-amber-300 bg-amber-50 rounded-xl p-4">
        <legend className="px-2 font-black text-amber-900 text-sm">⚠️ إجراءات جماعية</legend>
        <p className="text-xs text-amber-800 mb-3">كل الإجراءات أدناه ستُسجَّل في Audit Log + سيصل المسوّق إيميل بالإجراء.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <button
            onClick={() => bulkAction.mutate('pause')}
            disabled={bulkAction.isPending || filteredCount === 0}
            className="px-4 py-3 rounded-xl border-2 border-amber-300 bg-white hover:bg-amber-100 text-amber-900 font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            ⏸️ إيقاف مؤقت ({filteredCount})
          </button>
          <button
            onClick={() => bulkAction.mutate('unpause')}
            disabled={bulkAction.isPending || filteredCount === 0}
            className="px-4 py-3 rounded-xl border-2 border-green-300 bg-white hover:bg-green-100 text-green-800 font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            ▶️ إعادة تفعيل ({filteredCount})
          </button>
          <button
            onClick={() => bulkAction.mutate('archive')}
            disabled={bulkAction.isPending || filteredCount === 0}
            className="px-4 py-3 rounded-xl border-2 border-gray-300 bg-white hover:bg-gray-100 text-gray-700 font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            🗄️ أرشفة ({filteredCount})
          </button>
          {!confirmDelete ? (
            <button
              onClick={() => setConfirmDelete(true)}
              disabled={filteredCount === 0}
              className="px-4 py-3 rounded-xl border-2 border-red-300 bg-white hover:bg-red-100 text-red-700 font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              🗑️ حذف نهائي ({filteredCount})
            </button>
          ) : (
            <div className="grid grid-cols-2 gap-1">
              <button
                onClick={() => setConfirmDelete(false)}
                className="px-2 py-3 rounded-xl border-2 border-gray-300 bg-white hover:bg-gray-100 text-gray-700 font-bold text-xs transition"
              >
                إلغاء
              </button>
              <button
                onClick={() => bulkAction.mutate('delete')}
                disabled={bulkAction.isPending}
                className="px-2 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-xs transition"
              >
                💀 تأكيد الحذف
              </button>
            </div>
          )}
        </div>
        {bulkAction.isSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-2 mt-3 text-xs text-green-800">
            ✓ تم! تم تطبيق الإجراء على {bulkAction.data?.data?.affected ?? 0} إعلان وإشعار {brokerName} بالإيميل.
          </div>
        )}
        {bulkAction.isError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-2 mt-3 text-xs text-red-800">
            ⚠️ {(bulkAction.error as Error)?.message ?? 'فشل الإجراء'}
          </div>
        )}
      </fieldset>

      {/* 📋 قائمة آخر 10 إعلانات */}
      {isLoading ? (
        <div className="text-center py-8 text-ink-muted text-sm">⟳ جاري التحميل…</div>
      ) : properties.length === 0 ? (
        <div className="text-center py-8 text-ink-muted text-sm">لا توجد إعلانات لهذا المسوّق</div>
      ) : (
        <fieldset className="border border-gray-200 rounded-xl p-4">
          <legend className="px-2 font-black text-navy-dark text-sm">🏘️ آخر الإعلانات ({properties.length})</legend>
          <div className="mt-2 space-y-1.5 max-h-72 overflow-y-auto pr-1">
            {properties.slice(0, 20).map((p) => (
              <div key={p.id} className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 text-xs">
                {p.coverImage ? (
                  <img src={p.coverImage} alt="" className="w-8 h-8 rounded object-cover shrink-0" />
                ) : (
                  <div className="w-8 h-8 rounded bg-navy-soft flex items-center justify-center shrink-0 text-sm">🏠</div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-navy-dark truncate">{p.title}</div>
                  <div className="text-[10px] text-ink-muted">{p.city ?? '—'} · {Number(p.price).toLocaleString('en-US')} ر.س</div>
                </div>
                <Badge color={p.status === 'ACTIVE' ? 'green' : p.status === 'PENDING_REVIEW' ? 'amber' : 'gray'}>{p.status}</Badge>
              </div>
            ))}
          </div>
        </fieldset>
      )}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ تبويب الاشتراك ━━━━━━━━━━━━━━━━━━━━

function SubscriptionTab({ broker, onSaved }: { broker: BrokerRow; onSaved: () => void }) {
  const bp = broker.brokerProfile;
  const [plan, setPlan] = useState(bp?.subscriptionPlan ?? 'FREE');
  const [expires, setExpires] = useState(bp?.subscriptionExpiresAt ? bp.subscriptionExpiresAt.slice(0, 10) : '');

  const m = useMutation({
    mutationFn: () => api.put(`/api/admin/users/${broker.id}/subscription`, {
      plan,
      expiresAt: expires ? new Date(expires).toISOString() : null,
    }).catch(() =>
      // fallback في حال ما في endpoint مخصص
      api.put(`/api/admin/users/${broker.id}/broker-discount`, { plan }),
    ),
    onSuccess: onSaved,
  });

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm">
        <div className="text-xs text-blue-900 font-bold mb-1">الخطة الحالية</div>
        <div className="flex items-center justify-between">
          <Badge color={PLANS[bp?.subscriptionPlan ?? 'FREE']?.color ?? 'gray'}>
            {PLANS[bp?.subscriptionPlan ?? 'FREE']?.label}
          </Badge>
          {bp?.subscriptionExpiresAt && (
            <span className="text-xs text-ink-secondary">
              تنتهي: {new Date(bp.subscriptionExpiresAt).toLocaleDateString('ar-SA')}
            </span>
          )}
        </div>
      </div>

      <fieldset className="border border-gray-200 rounded-xl p-4">
        <legend className="px-2 font-black text-navy-dark text-sm">📝 تغيير الخطة</legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div>
            <label className="block text-sm font-bold text-ink mb-1">الخطة الجديدة</label>
            <select value={plan} onChange={(e) => setPlan(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white">
              {Object.entries(PLANS).map(([k, v]) => <option key={k} value={k}>{v.label} ({k})</option>)}
            </select>
          </div>
          <Input label="تاريخ الانتهاء" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
        </div>
      </fieldset>

      <div className="flex justify-end">
        <Button variant="primary" onClick={() => m.mutate()} loading={m.isPending}>💾 حفظ الاشتراك</Button>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ تبويب الخصم ━━━━━━━━━━━━━━━━━━━━

function DiscountTab({ broker, onSaved }: { broker: BrokerRow; onSaved: () => void }) {
  const bp = broker.brokerProfile;
  const [pct, setPct] = useState(bp?.subscriptionDiscountPct?.toString() ?? '');
  const [fixed, setFixed] = useState(bp?.subscriptionDiscountFixed?.toString() ?? '');
  const [until, setUntil] = useState(bp?.subscriptionDiscountUntil ? bp.subscriptionDiscountUntil.slice(0, 10) : '');
  const [reason, setReason] = useState(bp?.subscriptionDiscountReason ?? '');

  const m = useMutation({
    mutationFn: () => api.put(`/api/admin/users/${broker.id}/broker-discount`, {
      pct: pct ? Number(pct) : null,
      fixed: fixed ? Number(fixed) : null,
      untilDate: until ? new Date(until).toISOString() : null,
      reason: reason.trim() || null,
    }),
    onSuccess: onSaved,
  });

  if (!bp) return <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3">لا يوجد ملف مسوّق مرتبط بعد. أنشئه من تبويب الاشتراك.</div>;

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm">
        💡 الخصم يُطبَّق عند تجديد الاشتراك. يمكن أن يكون نسبة، أو مبلغاً ثابتاً، أو الاثنين.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input label="نسبة الخصم %" type="number" value={pct} onChange={(e) => setPct(e.target.value)} placeholder="مثل: 25" />
        <Input label="خصم ثابت (ر.س)" type="number" value={fixed} onChange={(e) => setFixed(e.target.value)} placeholder="مثل: 100" />
        <Input label="ساري حتى" type="date" value={until} onChange={(e) => setUntil(e.target.value)} />
        <Input label="سبب الخصم" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="عرض ترويجي، شراكة..." />
      </div>

      <div className="flex justify-between gap-2">
        <Button variant="ghost" onClick={() => { setPct(''); setFixed(''); setUntil(''); setReason(''); m.mutate(); }}>
          ✕ إلغاء الخصم
        </Button>
        <Button variant="primary" onClick={() => m.mutate()} loading={m.isPending}>💾 حفظ الخصم</Button>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ تبويب الأمان ━━━━━━━━━━━━━━━━━━━━

function SecurityTab({ broker, onSaved }: { broker: BrokerRow; onSaved: () => void }) {
  const [newPwd, setNewPwd] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [done, setDone] = useState(false);

  const resetM = useMutation({
    mutationFn: () => api.post(`/api/admin/users/${broker.id}/reset-password`, { newPassword: newPwd }),
    onSuccess: () => { setDone(true); setNewPwd(''); onSaved(); setTimeout(() => setDone(false), 4000); },
  });

  const verifyM = useMutation({
    mutationFn: () => api.put(`/api/admin/users/${broker.id}`, { verified: !broker.verified }),
    onSuccess: onSaved,
  });

  return (
    <div className="space-y-4">
      {/* تأكيد التحقق */}
      <fieldset className="border border-gray-200 rounded-xl p-4">
        <legend className="px-2 font-black text-navy-dark text-sm">⭐ التحقق</legend>
        <div className="flex items-center justify-between mt-2">
          <div>
            <div className="font-bold">{broker.verified ? '✓ موثّق' : '○ غير موثّق'}</div>
            <p className="text-xs text-ink-muted mt-1">المسوّقون الموثّقون يحظون بظهور أفضل وثقة العملاء.</p>
          </div>
          <Button variant={broker.verified ? 'ghost' : 'primary'} onClick={() => verifyM.mutate()} loading={verifyM.isPending}>
            {broker.verified ? 'إلغاء التوثيق' : '✓ توثيق'}
          </Button>
        </div>
      </fieldset>

      {/* إعادة كلمة المرور */}
      <fieldset className="border border-gray-200 rounded-xl p-4">
        <legend className="px-2 font-black text-navy-dark text-sm">🔑 إعادة تعيين كلمة المرور</legend>
        <div className="mt-2 space-y-3">
          <div className="relative">
            <Input
              label="كلمة المرور الجديدة (8 أحرف فأكثر)"
              type={showPwd ? 'text' : 'password'}
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              dir="ltr"
            />
            <button onClick={() => setShowPwd(!showPwd)} className="absolute left-3 top-9 text-xs text-ink-muted hover:text-navy-dark">
              {showPwd ? '🙈' : '👁️'}
            </button>
          </div>
          <p className="text-xs text-ink-muted">⚠️ سيتم إلغاء كل جلسات المسوّق الحالية وإجباره على تسجيل الدخول من جديد.</p>
          {done && <div className="bg-green-50 border border-green-200 text-green-800 text-xs rounded-lg p-2">✓ تمت إعادة التعيين — أبلغ المسوّق بكلمة المرور الجديدة بشكل آمن.</div>}
          <div className="flex justify-end">
            <Button
              variant="primary"
              onClick={() => resetM.mutate()}
              loading={resetM.isPending}
              disabled={newPwd.length < 8}
            >
              🔑 تعيين كلمة المرور
            </Button>
          </div>
        </div>
      </fieldset>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ تبويب الخصائص الإدارية ━━━━━━━━━━━━━━━━━━━━

function AdminPropsTab({ broker, onSaved }: { broker: BrokerRow & { adminNote?: string | null; customBadge?: string | null; customBadgeColor?: string | null; bidCap?: number | string | null; blacklisted?: boolean }; onSaved: () => void }) {
  const [adminNote, setAdminNote] = useState(broker.adminNote ?? '');
  const [customBadge, setCustomBadge] = useState(broker.customBadge ?? '');
  const [customBadgeColor, setCustomBadgeColor] = useState(broker.customBadgeColor ?? 'navy');
  const [tier, setTier] = useState(broker.verificationTier ?? 'NONE');

  const m = useMutation({
    mutationFn: () => api.put(`/api/admin/users/${broker.id}`, {
      adminNote: adminNote.trim() || null,
      customBadge: customBadge.trim() || null,
      customBadgeColor: customBadge.trim() ? customBadgeColor : null,
      verificationTier: tier === 'NONE' ? null : tier,
    }),
    onSuccess: onSaved,
  });

  return (
    <div className="space-y-4">
      <fieldset className="border border-gray-200 rounded-xl p-4">
        <legend className="px-2 font-black text-navy-dark text-sm">📝 ملاحظات إدارية (سرية)</legend>
        <textarea
          value={adminNote}
          onChange={(e) => setAdminNote(e.target.value)}
          rows={3}
          placeholder="ملاحظات داخلية لا يراها المسوّق…"
          className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm"
        />
      </fieldset>

      <fieldset className="border border-gray-200 rounded-xl p-4">
        <legend className="px-2 font-black text-navy-dark text-sm">🏆 شارة مخصّصة</legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
          <Input label="نص الشارة" value={customBadge} onChange={(e) => setCustomBadge(e.target.value)} placeholder="VIP، شريك..." />
          <div>
            <label className="block text-sm font-bold text-ink mb-1">لون الشارة</label>
            <select value={customBadgeColor} onChange={(e) => setCustomBadgeColor(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white">
              <option value="gold">🟡 ذهبي</option>
              <option value="green">🟢 أخضر</option>
              <option value="red">🔴 أحمر</option>
              <option value="navy">🔵 كحلي</option>
              <option value="amber">🟠 برتقالي</option>
            </select>
          </div>
        </div>
        {customBadge && (
          <div className="mt-3 text-sm">
            <span className="text-ink-muted ml-2">معاينة:</span>
            <Badge color={customBadgeColor as never}>{customBadge}</Badge>
          </div>
        )}
      </fieldset>

      <fieldset className="border border-gray-200 rounded-xl p-4">
        <legend className="px-2 font-black text-navy-dark text-sm">🥇 مستوى التوثيق</legend>
        <select value={tier} onChange={(e) => setTier(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white mt-2">
          <option value="NONE">بدون</option>
          <option value="GREEN">🟢 أخضر</option>
          <option value="GOLD">🟡 ذهبي</option>
          <option value="DIAMOND">💎 ماسي</option>
        </select>
      </fieldset>

      <div className="flex justify-end">
        <Button variant="primary" onClick={() => m.mutate()} loading={m.isPending}>💾 حفظ الخصائص</Button>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ الشكاوى ━━━━━━━━━━━━━━━━━━━━

function ComplaintsList() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'complaints', statusFilter],
    queryFn: () => api.get<Paginated<Complaint>>('/api/admin/brokers/complaints', { query: { status: statusFilter, limit: 50 } }),
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/brokers/complaints/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'complaints'] }),
  });

  const list = data?.data ?? [];
  const counts = {
    all: list.length,
    new: list.filter((c) => c.status === 'new').length,
    investigating: list.filter((c) => c.status === 'investigating').length,
    resolved: list.filter((c) => c.status === 'resolved').length,
  };

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="الإجمالي"      value={counts.all}           color="navy"  icon="🚨" active={!statusFilter} onClick={() => setStatusFilter('')} />
        <StatCard label="جديدة"          value={counts.new}           color="amber" icon="🆕" active={statusFilter === 'new'} onClick={() => setStatusFilter(statusFilter === 'new' ? '' : 'new')} pulse />
        <StatCard label="قيد التحقيق"   value={counts.investigating} color="navy"  icon="🔍" active={statusFilter === 'investigating'} onClick={() => setStatusFilter(statusFilter === 'investigating' ? '' : 'investigating')} />
        <StatCard label="محلولة"         value={counts.resolved}      color="green" icon="✓"  active={statusFilter === 'resolved'} onClick={() => setStatusFilter(statusFilter === 'resolved' ? '' : 'resolved')} />
      </div>

      {isLoading ? (
        <div className="text-gray-500">⟳ جاري التحميل…</div>
      ) : list.length === 0 ? (
        <Card><CardBody><div className="text-center py-12 text-gray-500">لا توجد شكاوى</div></CardBody></Card>
      ) : (
        <div className="space-y-3">
          {list.map((c) => (
            <Card key={c.id}>
              <CardBody>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[250px]">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Badge color={COMPLAINT_STATUS[c.status].color}>{COMPLAINT_STATUS[c.status].label}</Badge>
                      <span className="text-xs text-gray-500">{new Date(c.createdAt).toLocaleString('ar-SA')}</span>
                    </div>
                    <h3 className="font-black text-navy-dark mb-1">{c.subject}</h3>
                    <p className="text-sm text-gray-600 line-clamp-2">{c.details}</p>
                    <div className="text-xs text-gray-500 mt-2 grid grid-cols-2 gap-1">
                      <div>المشتكي: <b>{c.user.name}</b></div>
                      <div>المسوّق: <b>{c.broker.name}</b></div>
                    </div>
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    <Button size="sm" variant="primary" onClick={() => setOpenId(c.id)}>إدارة</Button>
                    <Button size="sm" variant="ghost" onClick={() => { if (confirm('حذف الشكوى؟')) deleteM.mutate(c.id); }}>🗑</Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {openId && data?.data && (
        <ComplaintModal
          complaint={data.data.find((c) => c.id === openId)!}
          onClose={() => setOpenId(null)}
          onSaved={() => qc.invalidateQueries({ queryKey: ['admin', 'complaints'] })}
        />
      )}
    </>
  );
}

function ComplaintModal({ complaint, onClose, onSaved }: { complaint: Complaint; onClose: () => void; onSaved: () => void }) {
  const [status, setStatus] = useState(complaint.status);
  const [notes, setNotes] = useState(complaint.adminNotes ?? '');
  const m = useMutation({
    mutationFn: () => api.put(`/api/admin/brokers/complaints/${complaint.id}`, { status, adminNotes: notes || undefined }),
    onSuccess: () => { onSaved(); onClose(); },
  });
  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="text-xl font-black text-navy-dark">إدارة الشكوى</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-gray-50 rounded-xl p-3">
              <div className="text-xs text-gray-500">المشتكي</div>
              <div className="font-bold">{complaint.user.name}</div>
              <div className="text-xs text-gray-500" dir="ltr">{complaint.user.email}</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <div className="text-xs text-gray-500">المسوّق</div>
              <div className="font-bold">{complaint.broker.name}</div>
              <div className="text-xs text-gray-500" dir="ltr">{complaint.broker.email}</div>
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-1">الموضوع</div>
            <div className="font-bold">{complaint.subject}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-1">التفاصيل</div>
            <div className="text-sm bg-gray-50 rounded-xl p-3 max-h-48 overflow-y-auto whitespace-pre-line">{complaint.details}</div>
          </div>
          <div>
            <label className="block text-sm font-bold text-ink mb-1">الحالة</label>
            <select value={status} onChange={(e) => setStatus(e.target.value as never)} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white">
              <option value="new">جديدة</option>
              <option value="investigating">قيد التحقيق</option>
              <option value="resolved">محلولة</option>
              <option value="rejected">مرفوضة</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-bold text-ink mb-1">ملاحظات إدارية</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm" placeholder="ملاحظات داخلية على الشكوى" />
          </div>
        </div>
        <div className="p-5 border-t flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>إلغاء</Button>
          <Button variant="primary" onClick={() => m.mutate()} loading={m.isPending}>💾 حفظ</Button>
        </div>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ طلبات التحوّل لمسوّق ━━━━━━━━━━━━━━━━━━━━

interface BrokerApplicationRow {
  id: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  fullName: string;
  phone: string;
  email: string | null;
  city: string;
  falLicenseNumber: string;
  falLicenseFile: string | null;
  falLicenseExpiry: string | null;
  yearsExperience: number;
  agencyName: string | null;
  citiesServed: string[] | null;
  specialties: string[] | null;
  motivation: string | null;
  rejectReason: string | null;
  reviewedAt: string | null;
  createdAt: string;
  user: { id: string; name: string; email: string | null; phone: string | null; image: string | null; role: string };
  reviewer: { id: string; name: string } | null;
}

const APP_STATUS: Record<string, { label: string; color: 'amber' | 'green' | 'red' | 'gray'; icon: string }> = {
  PENDING:   { label: 'قيد المراجعة', color: 'amber', icon: '⏳' },
  APPROVED:  { label: 'مُعتمد',        color: 'green', icon: '✅' },
  REJECTED:  { label: 'مرفوض',         color: 'red',   icon: '❌' },
  CANCELLED: { label: 'ملغى',          color: 'gray',  icon: '✕'  },
};

function ApplicationsList() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [openApprove, setOpenApprove] = useState<BrokerApplicationRow | null>(null);
  const [openReject, setOpenReject] = useState<BrokerApplicationRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'broker-applications', statusFilter],
    queryFn: () => api.get<Paginated<BrokerApplicationRow>>('/api/admin/brokers/applications', {
      query: { status: statusFilter, limit: 50 },
    }),
  });

  const list = data?.data ?? [];
  const counts = {
    pending:   list.filter((a) => a.status === 'PENDING').length,
    approved:  list.filter((a) => a.status === 'APPROVED').length,
    rejected:  list.filter((a) => a.status === 'REJECTED').length,
  };
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'broker-applications'] });

  return (
    <>
      {/* فلاتر سريعة */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="قيد المراجعة" value={counts.pending}  color="amber" icon="⏳"
          active={statusFilter === 'PENDING'} onClick={() => setStatusFilter('PENDING')} pulse />
        <StatCard label="معتمد"          value={counts.approved} color="green" icon="✅"
          active={statusFilter === 'APPROVED'} onClick={() => setStatusFilter('APPROVED')} />
        <StatCard label="مرفوض"          value={counts.rejected} color="navy"  icon="❌"
          active={statusFilter === 'REJECTED'} onClick={() => setStatusFilter('REJECTED')} />
        <StatCard label="عرض الكل"      value={list.length}      color="navy"  icon="📋"
          active={!statusFilter} onClick={() => setStatusFilter('')} />
      </div>

      {isLoading ? (
        <div className="text-gray-500">⟳ جاري التحميل…</div>
      ) : list.length === 0 ? (
        <Card><CardBody><div className="text-center py-12 text-gray-500">لا توجد طلبات</div></CardBody></Card>
      ) : (
        <div className="space-y-3">
          {list.map((a) => (
            <Card key={a.id}>
              <CardBody>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[280px]">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Badge color={APP_STATUS[a.status].color}>
                        {APP_STATUS[a.status].icon} {APP_STATUS[a.status].label}
                      </Badge>
                      <span className="text-xs text-gray-500">
                        {new Date(a.createdAt).toLocaleString('ar-SA')}
                      </span>
                    </div>

                    <h3 className="font-black text-navy-dark mb-1">{a.fullName}</h3>
                    <div className="text-xs text-gray-500 grid grid-cols-2 gap-1 mt-2">
                      <div>👤 المستخدم: <b>{a.user.name}</b></div>
                      <div>📍 المدينة: <b>{a.city}</b></div>
                      <div>📞 <span dir="ltr">{a.phone}</span></div>
                      {a.email && <div>📧 <span dir="ltr">{a.email}</span></div>}
                      <div>🏛️ رخصة فال: <b dir="ltr">{a.falLicenseNumber}</b></div>
                      <div>⏱ خبرة: <b>{a.yearsExperience} سنة</b></div>
                      {a.agencyName && <div className="col-span-2">🏢 المكتب: <b>{a.agencyName}</b></div>}
                      {a.specialties?.length ? (
                        <div className="col-span-2">🎯 التخصصات: <b>{a.specialties.join('، ')}</b></div>
                      ) : null}
                    </div>

                    {a.motivation && (
                      <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-700">
                        <b>الدافع:</b> {a.motivation}
                      </div>
                    )}

                    {a.rejectReason && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                        <b>سبب الرفض:</b> {a.rejectReason}
                      </div>
                    )}
                    {a.falLicenseFile && (
                      <a href={a.falLicenseFile} target="_blank" rel="noreferrer"
                         className="inline-block mt-2 text-xs text-gold font-bold hover:underline">
                        🏛️ عرض ملف الرخصة ↗
                      </a>
                    )}
                  </div>

                  {/* أزرار الإجراءات */}
                  {a.status === 'PENDING' && (
                    <div className="flex gap-1 flex-wrap">
                      <Button size="sm" variant="primary" onClick={() => setOpenApprove(a)}>✅ اعتماد</Button>
                      <Button size="sm" variant="ghost" onClick={() => setOpenReject(a)}>❌ رفض</Button>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {/* مودال الموافقة */}
      {openApprove && (
        <ApproveApplicationModal
          application={openApprove}
          onClose={() => setOpenApprove(null)}
          onSaved={() => { invalidate(); setOpenApprove(null); }}
        />
      )}
      {/* مودال الرفض */}
      {openReject && (
        <RejectApplicationModal
          application={openReject}
          onClose={() => setOpenReject(null)}
          onSaved={() => { invalidate(); setOpenReject(null); }}
        />
      )}
    </>
  );
}

function ApproveApplicationModal({
  application, onClose, onSaved,
}: {
  application: BrokerApplicationRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [reviewNotes, setReviewNotes] = useState('');
  const [initialPlanCode, setInitialPlanCode] = useState<string>('IND_STARTER');
  const m = useMutation({
    mutationFn: () => api.post(`/api/admin/brokers/applications/${application.id}/approve`, { reviewNotes, initialPlanCode }),
    onSuccess: onSaved,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-auto">
        <div className="p-5 border-b">
          <h3 className="font-black text-navy-dark text-lg">✅ اعتماد طلب التحوّل لمسوّق</h3>
          <p className="text-xs text-gray-500 mt-1">
            سيتم تحويل <b>{application.user.name}</b> إلى مسوّق وإنشاء ملفه التلقائي.
          </p>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">الخطة الأولية</label>
            <select
              value={initialPlanCode}
              onChange={(e) => setInitialPlanCode(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <optgroup label="👤 مسوّق فرد">
                <option value="IND_STARTER">المبتدئة (مجاناً)</option>
                <option value="IND_PRO">الاحترافية (299 ر.س/شهر)</option>
                <option value="IND_ELITE">النخبة (599 ر.س/شهر)</option>
              </optgroup>
              <optgroup label="🏢 شركة تسويق">
                <option value="CO_BASIC">شركة أساسية (1,499 ر.س/شهر)</option>
                <option value="CO_PRO">شركة متقدّمة (2,999 ر.س/شهر)</option>
                <option value="CO_ENTERPRISE">شركة مؤسسية VIP (4,999 ر.س/شهر)</option>
              </optgroup>
            </select>
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">ملاحظات مراجعة (اختياري)</label>
            <textarea
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg min-h-[80px]"
              placeholder="ملاحظات داخلية للسجل…"
            />
          </div>
          {m.isError && (
            <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
              ⚠️ {(m.error as Error).message || 'حدث خطأ'}
            </div>
          )}
        </div>
        <div className="p-5 border-t flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>إلغاء</Button>
          <Button variant="primary" onClick={() => m.mutate()} loading={m.isPending}>✅ تأكيد الاعتماد</Button>
        </div>
      </div>
    </div>
  );
}

function RejectApplicationModal({
  application, onClose, onSaved,
}: {
  application: BrokerApplicationRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: () => api.post(`/api/admin/brokers/applications/${application.id}/reject`, { reason }),
    onSuccess: onSaved,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-auto">
        <div className="p-5 border-b">
          <h3 className="font-black text-red-700 text-lg">❌ رفض طلب التحوّل لمسوّق</h3>
          <p className="text-xs text-gray-500 mt-1">
            سيتم إعلام <b>{application.user.name}</b> بالسبب عبر إشعار داخلي.
          </p>
        </div>
        <div className="p-5">
          <label className="block text-sm font-bold text-gray-700 mb-1">سبب الرفض *</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg min-h-[120px]"
            placeholder="مثلاً: رخصة فال غير سارية، أو نقص بيانات، أو مخالفات قضائية…"
          />
          {m.isError && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
              ⚠️ {(m.error as Error).message || 'حدث خطأ'}
            </div>
          )}
        </div>
        <div className="p-5 border-t flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>إلغاء</Button>
          <Button
            variant="primary"
            disabled={reason.trim().length < 5}
            onClick={() => m.mutate()}
            loading={m.isPending}
            className="!bg-red-600 hover:!bg-red-700"
          >
            ❌ تأكيد الرفض
          </Button>
        </div>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ التقييمات ━━━━━━━━━━━━━━━━━━━━

function ReviewsList() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'reviews'],
    queryFn: () => api.get<Paginated<Review>>('/api/admin/brokers/reviews', { query: { limit: 50 } }),
  });
  const deleteM = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/brokers/reviews/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'reviews'] }),
  });

  if (isLoading) return <div className="text-gray-500">⟳ جاري التحميل…</div>;
  if (!data?.data?.length) return <Card><CardBody><div className="text-center py-12 text-gray-500">لا توجد تقييمات</div></CardBody></Card>;

  return (
    <div className="space-y-3">
      {data.data.map((r) => (
        <Card key={r.id}>
          <CardBody>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <div className="text-amber-500">{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</div>
                  <span className="text-xs text-gray-500">{new Date(r.createdAt).toLocaleString('ar-SA')}</span>
                </div>
                {r.review && <p className="text-sm text-gray-700 mb-2">{r.review}</p>}
                <div className="text-xs text-gray-500">
                  من <b>{r.user.name}</b> إلى <b>{r.broker.name}</b>
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => { if (confirm('حذف التقييم؟')) deleteM.mutate(r.id); }}>🗑</Button>
            </div>
          </CardBody>
        </Card>
      ))}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ Sub-components ━━━━━━━━━━━━━━━━━━━━

function Tab({ children, active, onClick }: { children: React.ReactNode; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} className={`px-4 py-2 text-sm font-bold transition border-b-2 -mb-px whitespace-nowrap ${
      active ? 'border-gold text-navy-dark' : 'border-transparent text-gray-500 hover:text-navy-dark'
    }`}>{children}</button>
  );
}

function SubTab({ children, active, onClick }: { children: React.ReactNode; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} className={`px-4 py-3 text-xs font-bold transition border-b-2 -mb-px whitespace-nowrap ${
      active ? 'border-green-500 text-green-700' : 'border-transparent text-gray-500 hover:text-navy-dark'
    }`}>{children}</button>
  );
}

function StatCard({ label, value, color, icon, active, onClick, pulse }: {
  label: string; value: number; color: 'navy' | 'amber' | 'green' | 'red' | 'gold' | 'gray';
  icon: string; active?: boolean; onClick?: () => void; pulse?: boolean;
}) {
  const colors = {
    navy:  { active: 'border-navy bg-navy-soft',     text: 'text-navy-dark' },
    amber: { active: 'border-amber-400 bg-amber-50', text: 'text-amber-700' },
    green: { active: 'border-green-400 bg-green-50', text: 'text-green-700' },
    red:   { active: 'border-red-400 bg-red-50',     text: 'text-red-700' },
    gold:  { active: 'border-gold bg-gold-soft',     text: 'text-gold-dark' },
    gray:  { active: 'border-gray-400 bg-gray-100',  text: 'text-gray-700' },
  }[color];
  const Comp: React.ElementType = onClick ? 'button' : 'div';
  return (
    <Comp
      onClick={onClick}
      className={`text-right p-3 rounded-2xl border-2 bg-white transition ${onClick ? 'hover:shadow-brand cursor-pointer' : ''} ${
        active ? colors.active : 'border-gray-200'
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="text-xl">{icon}</span>
        {pulse && value > 0 && <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />}
      </div>
      <div className={`text-2xl font-black mt-1 ${colors.text}`}>{value}</div>
      <div className="text-[11px] text-ink-muted">{label}</div>
    </Comp>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="bg-gray-50 rounded-lg py-2 px-2 border border-gray-100">
      <div className="text-[10px] text-ink-muted">{label}</div>
      <div className="text-sm font-black text-navy-dark">{value}</div>
    </div>
  );
}
